/**
 * Right-side keystone tracker in the quest-tracker style: dungeon + key level, affix icons, a big
 * timer with the +3 / +2 chest thresholds, the enemy-forces bar, the boss checklist and deaths.
 * Borderless panel fading toward the screen centre, thin gold rule on top.
 *
 * Per frame: fillTrackerView (allocation-free) → write-if-changed DOM. Text is rebuilt only when
 * the shown whole seconds / tenths change; bars are scaleX transforms from the interned table.
 */
import type { HudUpdate } from '../hud';
import { el, setClass, setStyle, setText } from '../dom';
import { SCALE_X, StatBar, barIndex, glyphSvg, intText, svgEl } from '../kit';
import { attachAbilityTooltip, describeText } from '../tooltip';
import type { HudWidget } from '../widgets';
import { fieldView } from '../../world/fieldView'; // +field
import { type TrackerView, CHEST_THRESHOLDS, affixInfo, clockText, emptyTrackerView, fillTrackerView } from './trackerModel';

/** "62.4%" for forcesTenths 0..1000. */
const FORCE_PCT: readonly string[] = Array.from({ length: 1001 }, (_, i) => `${(i / 10).toFixed(1)}%`);

const AFFIX_GLYPH: Readonly<Record<string, string>> = {
  // Fortified: a stacked-stone tower. Tyrannical: a crowned skull.
  fortified: `<path fill="currentColor" fill-opacity="0.25" d="M6 20V9h2V6h2v3h4V6h2v3h2v11z"/><path d="M6 13h12M6 16.5h12M10 20v-3.5M14 20v-3.5M12 13V9"/>`,
  tyrannical: `<path fill="currentColor" fill-opacity="0.25" d="M12 8.5c-3.6 0-6 2.4-6 5.6 0 1.8.9 3 2.2 3.8V20h7.6v-2.1c1.3-.8 2.2-2 2.2-3.8 0-3.2-2.4-5.6-6-5.6z"/><path d="M6.5 7.5 8 3.5l2 2.5L12 3l2 3 2-2.5 1.5 4"/><circle cx="9.8" cy="14" r="1.2"/><circle cx="14.2" cy="14" r="1.2"/>`,
};

function affixIcon(id: string): SVGSVGElement {
  const body = AFFIX_GLYPH[id];
  if (body) return svgEl(`<svg class="hk-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">${body}</svg>`);
  const letter = (affixInfo(id).name[0] ?? '?').toUpperCase();
  return svgEl(`<svg class="hk-glyph" viewBox="0 0 24 24" aria-hidden="true"><text x="12" y="16.5" text-anchor="middle" font-size="13" font-weight="700" fill="currentColor">${letter}</text></svg>`);
}

interface BossRow {
  root: HTMLElement;
  name: HTMLElement;
}

export class KeystoneTracker implements HudWidget {
  readonly root: HTMLElement;
  private readonly view: TrackerView = emptyTrackerView();
  private readonly name: HTMLElement;
  private readonly level: HTMLElement;
  private readonly affixes: HTMLElement;
  private readonly time: HTMLElement;
  private readonly total: HTMLElement;
  private readonly timeFill: HTMLElement;
  private readonly chest3: HTMLElement;
  private readonly chest3Time: HTMLElement;
  private readonly chest2: HTMLElement;
  private readonly chest2Time: HTMLElement;
  private readonly forcesPct: HTMLElement;
  private readonly forcesCount: HTMLElement;
  private readonly forcesBar: StatBar;
  private readonly bossBox: HTMLElement;
  private readonly bossRows: BossRow[] = [];
  private readonly deaths: HTMLElement;
  private readonly deathCount: HTMLElement;
  private readonly penalty: HTMLElement;
  private readonly wipes: HTMLElement;
  private readonly hint: HTMLElement;
  private affixIds: string[] = [];
  private affixBuilt = false;
  private shownLevel = -1;
  private shownForces = -1;
  private shownForcesReq = -1;
  private shownWipes = -1;
  /** Last shown whole seconds (elapsed, timer, +3 left, +2 left) and penalty seconds. */
  private secElapsed = -1;
  private secTimer = -1;
  private sec3 = -2;
  private sec2 = -2;
  private secPenalty = -1;
  private fieldHidden = false;

  constructor(host: HTMLElement) {
    const root = (this.root = el('section', 'hk-tracker hk-panel-fade', host));
    root.setAttribute('aria-label', 'Keystone');
    const head = el('div', 'trk-head', root);
    head.appendChild(glyphSvg('key', 'trk-key'));
    this.name = el('div', 'trk-name hk-title gold', head);
    this.level = el('div', 'trk-level hk-num', head);
    this.affixes = el('div', 'trk-affixes', root);

    const timer = el('div', 'trk-timer', root);
    this.time = el('span', 'trk-time hk-num', timer);
    this.total = el('span', 'trk-total hk-num', timer);
    const tb = el('div', 'trk-timebar', root);
    this.timeFill = el('div', 'trk-timebar-fill', tb);
    el('div', 'trk-mark', tb).style.left = `${CHEST_THRESHOLDS.plus3 * 100}%`;
    el('div', 'trk-mark', tb).style.left = `${CHEST_THRESHOLDS.plus2 * 100}%`;
    const chests = el('div', 'trk-chests', root);
    this.chest3 = el('span', 'trk-chest', chests);
    el('b', '', this.chest3, '+3');
    this.chest3Time = el('span', 'hk-num', this.chest3);
    this.chest2 = el('span', 'trk-chest', chests);
    el('b', '', this.chest2, '+2');
    this.chest2Time = el('span', 'hk-num', this.chest2);

    el('div', 'hk-rule', root);
    const fh = el('div', 'trk-forces-head', root);
    el('span', 'trk-label', fh, 'Enemy Forces');
    this.forcesCount = el('span', 'trk-forces-count hk-num', fh);
    this.forcesPct = el('span', 'trk-forces-pct hk-num', fh);
    this.forcesBar = new StatBar(root, { color: 'linear-gradient(180deg, #d9c2ff, #9a6cf5 55%, #5a34b0)', text: 'none', height: 'sm', ghost: false, className: 'trk-forces' });

    this.bossBox = el('ul', 'trk-bosses', root);
    const foot = el('div', 'trk-foot', root);
    this.deaths = el('span', 'trk-deaths', foot);
    this.deaths.appendChild(glyphSvg('skull'));
    this.deathCount = el('span', 'hk-num', this.deaths);
    this.penalty = el('span', 'trk-penalty hk-num', this.deaths);
    this.deaths.title = 'Deaths (each adds 5 s to the timer)';
    this.wipes = el('span', 'trk-wipes', foot);
    this.hint = el('div', 'trk-hint', root);
  }

  update(h: HudUpdate): void {
    // +field: the quest tracker (hud/field) takes this spot while a world session runs.
    const world = fieldView.active;
    if (world !== this.fieldHidden) {
      this.fieldHidden = world;
      this.root.classList.toggle('hk-hidden', world);
    }
    if (world) return;
    const v = fillTrackerView(h.world, h.content, this.view);
    setText(this.name, v.dungeon);
    if (v.level !== this.shownLevel) {
      this.shownLevel = v.level;
      setText(this.level, v.level > 0 ? `+${intText(v.level)}` : '');
    }
    this.syncAffixes(v.affixes);

    // Timer: elapsed big, total small; red when over.
    const se = Math.floor(v.elapsedMs / 1000);
    if (se !== this.secElapsed) {
      this.secElapsed = se;
      setText(this.time, clockText(v.elapsedMs));
    }
    const st = Math.floor(v.timerMs / 1000);
    if (st !== this.secTimer) {
      this.secTimer = st;
      setText(this.total, `/ ${clockText(v.timerMs)}`);
    }
    setClass(this.root, 'over', v.over);
    setClass(this.root, 'done', v.phase === 'completed' || v.phase === 'depleted');
    setStyle(this.timeFill, 'transform', SCALE_X[barIndex(v.timeFrac)]!);
    setClass(this.timeFill, 'p2', v.chest === 2);
    setClass(this.timeFill, 'p1', v.chest <= 1);
    const s3 = v.plus3LeftMs > 0 ? Math.ceil(v.plus3LeftMs / 1000) : -1;
    if (s3 !== this.sec3) {
      this.sec3 = s3;
      setText(this.chest3Time, s3 >= 0 ? clockText(v.plus3LeftMs + 999) : '—');
      setClass(this.chest3, 'missed', s3 < 0);
    }
    const s2 = v.plus2LeftMs > 0 ? Math.ceil(v.plus2LeftMs / 1000) : -1;
    if (s2 !== this.sec2) {
      this.sec2 = s2;
      setText(this.chest2Time, s2 >= 0 ? clockText(v.plus2LeftMs + 999) : '—');
      setClass(this.chest2, 'missed', s2 < 0);
    }

    // Forces.
    setText(this.forcesPct, FORCE_PCT[v.forcesTenths]!);
    if (v.forces !== this.shownForces || v.forcesRequired !== this.shownForcesReq) {
      this.shownForces = v.forces;
      this.shownForcesReq = v.forcesRequired;
      setText(this.forcesCount, v.forcesRequired > 0 ? `${intText(v.forces)}/${intText(v.forcesRequired)}` : '');
    }
    this.forcesBar.update(v.forces, Math.max(1, v.forcesRequired), 0);
    setClass(this.root, 'forces-done', v.forcesDone);

    // Bosses.
    if (this.bossRows.length !== v.bosses.length) this.buildBosses(v);
    for (let i = 0; i < v.bosses.length; i++) {
      const b = v.bosses[i]!;
      const row = this.bossRows[i]!;
      setText(row.name, b.name);
      setClass(row.root, 'killed', b.killed);
      setClass(row.root, 'engaged', b.engaged);
    }

    // Deaths / penalty / wipes.
    setText(this.deathCount, intText(v.deaths));
    const sp = Math.round(v.penaltyMs / 1000);
    if (sp !== this.secPenalty) {
      this.secPenalty = sp;
      setText(this.penalty, sp > 0 ? `−${intText(sp)}s` : '');
    }
    setClass(this.deaths, 'has', v.deaths > 0);
    if (v.wipes !== this.shownWipes) {
      this.shownWipes = v.wipes;
      setText(this.wipes, v.wipes > 0 ? (v.wipes === 1 ? '1 wipe' : `${intText(v.wipes)} wipes`) : '');
    }
    setText(this.hint, v.phase === 'pre' ? 'Move to start the timer' : '');
  }

  private syncAffixes(ids: readonly string[]): void {
    // Affixes are fixed for a run: rebuild only when the list changes (compared without allocating).
    let same = this.affixBuilt && this.affixIds.length === ids.length;
    for (let i = 0; same && i < ids.length; i++) if (this.affixIds[i] !== ids[i]) same = false;
    if (same) return;
    this.affixBuilt = true;
    this.affixIds = ids.slice();
    this.affixes.replaceChildren();
    for (const id of ids) {
      const chip = el('span', 'trk-affix hk-hit', this.affixes);
      chip.appendChild(affixIcon(id));
      const info = affixInfo(id);
      chip.setAttribute('aria-label', info.name);
      attachAbilityTooltip(chip, () => describeText(info.name, info.text, { tag: 'Affix' }));
    }
  }

  private buildBosses(v: TrackerView): void {
    this.bossBox.replaceChildren();
    this.bossRows.length = 0;
    for (let i = 0; i < v.bosses.length; i++) {
      const root = el('li', 'trk-boss', this.bossBox);
      el('span', 'trk-check', root);
      const name = el('span', 'trk-boss-name', root);
      this.bossRows.push({ root, name });
    }
  }
}
